import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import type { ModuleEntry } from '@/config/moduleEntries';
import { CHOOSE_APP_PATH } from '@/constants/authRoutes';
import { AuthButton } from '@/core/auth/components/AuthButton';
import { SsoAuthButtons } from '@/core/auth/components/SsoAuthButtons';
import { AuthCard } from '@/core/auth/components/AuthCard';
import { AuthInput } from '@/core/auth/components/AuthInput';
import { STORAGE_KEYS } from '@/constants/storage';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { clearAuth, login, verifyMfaLogin } from '@/store/authSlice';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { clearPlatformOnboardingRequired } from '@/modules/onboarding/onboardingState';
import { BRAND, fattahProjectTitle } from '@/config/brand';

const schema = z.object({
  email: z.string().email('Enter a valid work email'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional(),
});

type FormData = z.infer<typeof schema>;

export interface LoginFormProps {
  moduleEntry?: ModuleEntry;
  onLoginSuccess: (result: {
    masterAdmin: boolean;
    permissions?: string[];
    enabledModules?: string[];
    entryModule?: string | null;
  }) => void;
  showRegisterLink?: boolean;
  showAdminHint?: boolean;
  variant?: 'organization' | 'master';
}

export function LoginForm({
  moduleEntry,
  onLoginSuccess,
  showRegisterLink = true,
  showAdminHint = false,
  variant = 'organization',
}: LoginFormProps) {
  const dispatch = useAppDispatch();
  const loading = useAppSelector((s) => s.auth.loading);
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const ssoError = searchParams.get('ssoError');
  const entryModule = moduleEntry?.erpModule ?? moduleEntry?.slug;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema), defaultValues: { rememberMe: true } });

  useEffect(() => {
    if (!ssoError) return;
    const message =
      ssoError === 'access_denied'
        ? 'Google sign-in was cancelled'
        : ssoError === 'sso_failed'
          ? 'Google sign-in failed. Use email and password, or create an account first.'
          : 'Google sign-in could not be completed';
    notify.error(message);
    searchParams.delete('ssoError');
    setSearchParams(searchParams, { replace: true });
  }, [ssoError, searchParams, setSearchParams]);

  useEffect(() => {
    if (entryModule) {
      sessionStorage.setItem(STORAGE_KEYS.PENDING_ENTRY_MODULE, entryModule);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
    }
  }, [entryModule]);

  const onSubmit = async (data: FormData) => {
    try {
      const result = await dispatch(
        login({
          email: data.email,
          password: data.password,
          rememberMe: data.rememberMe,
          entryModule,
        }),
      ).unwrap();
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        notify.info('Enter the code from your authenticator app', { icon: '🔐' });
        return;
      }
      if (variant === 'master' && !result.masterAdmin) {
        dispatch(clearAuth());
        notify.error('This account is not a platform master administrator');
        return;
      }
      if (moduleEntry) {
        clearPlatformOnboardingRequired();
      }
      notify.success('Welcome back');
      onLoginSuccess(result);
    } catch (err) {
      showApiError(err, 'Invalid email or password');
    }
  };

  const onMfaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfaToken || totpCode.length < 6) return;
    try {
      const result = await dispatch(
        verifyMfaLogin({ mfaToken, totpCode, entryModule }),
      ).unwrap();
      if (variant === 'master' && !result.masterAdmin) {
        dispatch(clearAuth());
        notify.error('This account is not a platform master administrator');
        return;
      }
      if (moduleEntry) {
        clearPlatformOnboardingRequired();
      }
      notify.success('Welcome back');
      onLoginSuccess(result);
    } catch (err) {
      showApiError(err, 'Invalid authenticator code');
    }
  };

  const title =
    variant === 'master'
      ? fattahProjectTitle('Master Admin')
      : moduleEntry
        ? ''
        : BRAND.name;
  const subtitle =
    variant === 'master'
      ? 'Platform operator sign in'
      : moduleEntry
        ? 'Sign in to your workspace'
        : 'Sign in to your organization';

  if (mfaToken) {
    return (
      <AuthCard
        title={moduleEntry ? '' : BRAND.name}
        subtitle="Two-factor authentication"
      >
        <form onSubmit={onMfaSubmit} className="space-y-4">
          <AuthInput
            label="Authenticator code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <AuthButton type="submit" loading={loading} disabled={totpCode.length < 6}>
            Verify
          </AuthButton>
          <button
            type="button"
            className="w-full text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
            onClick={() => {
              setMfaToken(null);
              setTotpCode('');
            }}
          >
            Back to sign in
          </button>
        </form>
      </AuthCard>
    );
  }

  return (
    <>
      <AuthCard title={title} subtitle={subtitle}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <AuthInput label="Work email" type="email" autoComplete="email" {...register('email')} error={errors.email?.message} />
          <AuthInput
            label="Password"
            type="password"
            autoComplete="current-password"
            {...register('password')}
            error={errors.password?.message}
          />
          <div className="flex items-center justify-between gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <input type="checkbox" className="rounded border-slate-300 text-primary-600 focus:ring-primary-500" {...register('rememberMe')} />
              Remember me
            </label>
            <Link to="/forgot-password" className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400">
              Forgot password?
            </Link>
          </div>
          <AuthButton type="submit" loading={loading}>
            Sign in
          </AuthButton>
          <SsoAuthButtons
            redirectAfter={moduleEntry?.homePath ?? '/dashboard/home'}
            entryModule={entryModule}
            googleLabel="Sign in with Google"
          />
        </form>
        {showAdminHint && (
          <p className="mt-4 text-center text-xs text-slate-500 dark:text-slate-400">
            Need a different module?{' '}
            <Link to={CHOOSE_APP_PATH} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
              Choose another sign-in
            </Link>
          </p>
        )}
        {showRegisterLink && (
          <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
            New organization?{' '}
            <Link
              to={moduleEntry?.registerPath ?? '/register'}
              className="font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              Create account
            </Link>
          </p>
        )}
      </AuthCard>
    </>
  );
}
